import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import type { CartStockRemoval } from '@/features/Cart/types/models';
import type {
  AddToCartItem,
  ShopifyCart,
  ShopifyCartItem,
} from '@/features/Cart/types/servicesShopifyCart';
import { getLineQuantities } from '@/features/Cart/services/lineItemProperties';
import { getLinesByIdentity, getLineTitles } from '@/features/Cart/services/cartMutationResults';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import {
  getAddStockDemand,
  getCartStockUsage,
} from '@/features/Product/services/productAvailability';

const STOCK_RECHECK_DELAY = 3000;

let stockRecheckTimer: ReturnType<typeof setTimeout> | null = null;
let pendingStockCorrection: Map<string, CartStockRemoval> | null = null;

export const cartStockCorrection = () => {
  const cartStore = useCartStore();
  const { addCartStockRemovals } = cartStore;
  const { cartData } = storeToRefs(cartStore);
  const { refreshStock, getExactStock, getUnavailableComponentTitles } = useProductStockStore();

  const trackStockCorrection = (quantitiesBefore: Map<string, number>) => {
    if (!quantitiesBefore.size) return;
    const cartLines = getLinesByIdentity(cartData.value.items);
    const linesBefore = [...quantitiesBefore].map(
      ([lineIdentity, quantity]): [string, CartStockRemoval] => {
        const line = cartLines.get(lineIdentity);
        const unavailableComponentTitles = line
          ? getUnavailableComponentTitles({
              variantId: line.variant_id,
              properties: line.properties,
            })
          : [];
        return [lineIdentity, { title: line?.title ?? '', quantity, unavailableComponentTitles }];
      },
    );
    pendingStockCorrection = new Map([...linesBefore, ...(pendingStockCorrection ?? [])]);
  };

  const resolveStockCorrection = (cart: ShopifyCart) => {
    if (!pendingStockCorrection) return;
    const quantitiesAfter = getLineQuantities(cart.items);
    const titlesAfter = getLineTitles(cart.items);
    const removals = [...pendingStockCorrection].flatMap(([lineIdentity, lineBefore]) => {
      const removedQuantity = lineBefore.quantity - (quantitiesAfter.get(lineIdentity) ?? 0);
      if (removedQuantity <= 0) return [];
      const title = lineBefore.title || titlesAfter.get(lineIdentity) || '';
      const { unavailableComponentTitles } = lineBefore;
      return [{ title, quantity: removedQuantity, unavailableComponentTitles }];
    });
    pendingStockCorrection = null;
    if (removals.length) addCartStockRemovals(removals);
  };

  const isStockRejectionExplained = (
    items: AddToCartItem[],
    cartItemsBefore: ShopifyCartItem[],
  ): boolean => {
    const cartUsage = getCartStockUsage(cartItemsBefore);
    return [...getAddStockDemand(items)].some(([variantId, demand]) => {
      const exactStock = getExactStock(variantId);
      return exactStock !== undefined && demand + (cartUsage.get(variantId) ?? 0) > exactStock;
    });
  };

  const refreshStockAfterRejection = (
    items: AddToCartItem[],
    cartItemsBefore: ShopifyCartItem[],
  ) => {
    const isExplained = isStockRejectionExplained(items, cartItemsBefore);
    refreshStock({ force: true });
    if (isExplained || stockRecheckTimer) return;
    stockRecheckTimer = setTimeout(() => {
      stockRecheckTimer = null;
      refreshStock({ force: true });
    }, STOCK_RECHECK_DELAY);
  };

  return { trackStockCorrection, resolveStockCorrection, refreshStockAfterRejection };
};
